/* ─────────────────────────────────────────────────────────────
   PBP Cart Drawer — Pure By Priyanka
   Fixes Dawn theme cart redirect on icon click & Add to Cart.
   Auto-adds/removes the free Surprise Gift once the cart crosses
   the ₹1,999 goal (read from data attributes on #PBP-CartDrawer).
───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  /* ── 1. Stub Dawn's custom elements so cart.js never redirects ── */
  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', class extends HTMLElement {
      open()  { window.PBPCart && PBPCart.open();  }
      close() { window.PBPCart && PBPCart.close(); }
      renderContents() { window.PBPCart && PBPCart.refresh(); }
    });
  }
  if (!customElements.get('cart-drawer-items')) {
    customElements.define('cart-drawer-items', class extends HTMLElement {
      getSectionsToRender() { return []; }
    });
  }

  /* ── 2. Save reference to native fetch BEFORE any override ── */
  const _fetch = window.fetch.bind(window);

  /* ── 3. Intercept fetch — catch EXTERNAL /cart/add calls only
         (skip when we ourselves are mid-mutation, so gift add/remove
         and quick-add don't double-trigger a refresh) ── */
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0]
              : (args[0] instanceof Request ? args[0].url : '');
    const promise = _fetch(...args);

    if (url && url.includes('/cart/add') && !(window.PBPCart && PBPCart._internalAdd)) {
      promise.then(() => {
        setTimeout(() => {
          if (window.PBPCart) {
            PBPCart.syncCart().then(() => PBPCart.open());
          }
        }, 60);
      }).catch(() => {});
    }
    return promise;
  };

  /* ── 4. Only block exact /cart link clicks — nothing else ── */
  document.addEventListener('click', function (e) {
    const link = e.target.closest('a');
    if (!link) return;
    const href = (link.getAttribute('href') || '').trim();
    if (href === '/cart') {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (window.PBPCart) PBPCart.open();
    }
  }, true);

  /* ── 5. PBPCart object ── */
  window.PBPCart = {

    sectionId: 'cart-drawer',
    _internalAdd: false,

    getGiftConfig() {
      const el = document.getElementById('PBP-CartDrawer');
      const giftVariantId = parseInt(el?.dataset.giftVariantId || '0', 10);
      const goalCents = parseInt(el?.dataset.goalCents || '0', 10);
      return { giftVariantId, goalCents };
    },

    open() {
      document.getElementById('PBP-CartDrawer')?.classList.add('pbp-active');
      document.getElementById('PBP-CartOverlay')?.classList.add('pbp-active');
      document.body.style.overflow = 'hidden';
      setTimeout(() => this.updateNavBtns(), 80);
    },

    close() {
      document.getElementById('PBP-CartDrawer')?.classList.remove('pbp-active');
      document.getElementById('PBP-CartOverlay')?.classList.remove('pbp-active');
      document.body.style.overflow = '';
    },

    spin(on) {
      document.getElementById('PBP-Spinner')?.classList.toggle('pbp-show', on);
    },

    applySectionHtml(html) {
      if (!html) return false;
      const doc       = new DOMParser().parseFromString(html, 'text/html');
      const newInner  = doc.getElementById('PBP-CartDrawer');
      const curDrawer = document.getElementById('PBP-CartDrawer');
      if (!newInner || !curDrawer) return false;

      const wasActive = curDrawer.classList.contains('pbp-active');
      curDrawer.innerHTML = newInner.innerHTML;
      curDrawer.dataset.giftVariantId = newInner.dataset.giftVariantId;
      curDrawer.dataset.goalCents = newInner.dataset.goalCents;
      if (wasActive) curDrawer.classList.add('pbp-active');

      document.getElementById('PBP-Carousel')
        ?.addEventListener('scroll', () => this.updateNavBtns());

      this.updateNavBtns();
      this.updateBubble();
      return true;
    },

    /* GET fallback — used when we only observe a cart mutation and
       don't get section HTML back directly. */
    async refresh() {
      this.spin(true);
      try {
        const url = new URL(window.location.pathname + window.location.search, window.location.origin);
        url.searchParams.set('sections', this.sectionId);
        const res  = await _fetch(url.toString());
        const data = await res.json();
        this.applySectionHtml(data[this.sectionId]);
      } catch (err) {
        console.warn('[PBPCart] refresh failed', err);
      } finally {
        this.spin(false);
      }
    },

    /* Refresh the drawer, then check whether the free gift needs to be
       added or removed based on the new cart state. Always use this
       (not refresh() alone) after anything that could change the
       qualifying subtotal. */
    async syncCart() {
      await this.refresh();
      await this.checkGiftEligibility();
    },

    /* Reads live cart state, computes how many gifts should be in the
       cart (1 per ₹1,999 of qualifying subtotal — e.g. ₹3,998 = 2 gifts),
       and sets the gift line to that exact quantity via /cart/change.js
       (absolute set, not increment — safe to call repeatedly).

       Wrapped in a simple lock: if a check is already running when this
       is called again, we just flag a re-run for right after, instead of
       letting two checks race and both call /cart/add.js at once (which
       was causing the gift to be added twice). */
    async checkGiftEligibility() {
      if (this._giftCheckRunning) {
        this._giftCheckQueued = true;
        return;
      }
      this._giftCheckRunning = true;
      try {
        await this._runGiftCheck();
      } finally {
        this._giftCheckRunning = false;
        if (this._giftCheckQueued) {
          this._giftCheckQueued = false;
          await this.checkGiftEligibility();
        }
      }
    },

    async _runGiftCheck() {
      const { giftVariantId, goalCents } = this.getGiftConfig();

      if (!giftVariantId || !goalCents) {
        console.warn(
          '[PBPCart] Gift eligibility check skipped — missing config.',
          '\n  giftVariantId:', giftVariantId, '(0 means the "surprise-gift" product/variant was not found by Liquid)',
          '\n  goalCents:', goalCents,
          '\n  → Check that a product with handle "surprise-gift" exists, is Active, and is published to the Online Store sales channel.'
        );
        return;
      }

      try {
        const cart = await _fetch('/cart.js').then(r => r.json());
        const giftLine = cart.items.find(i => i.variant_id === giftVariantId);
        const qualifyingSubtotal = cart.items
          .filter(i => i.variant_id !== giftVariantId)
          .reduce((sum, i) => sum + i.original_line_price, 0);

        const desiredQty = Math.floor(qualifyingSubtotal / goalCents);

        console.info(
          '[PBPCart] Gift eligibility:',
          '\n  qualifyingSubtotal (paise):', qualifyingSubtotal,
          '\n  goalCents:', goalCents,
          '\n  desiredQty:', desiredQty,
          '\n  currentQty in cart:', giftLine ? giftLine.quantity : 0
        );

        if (desiredQty > 0 && !giftLine) {
          /* No gift line yet — add it once, at the full desired quantity. */
          this._internalAdd = true;
          const addRes = await _fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: giftVariantId, quantity: desiredQty })
          });
          const addData = await addRes.json();
          if (!addRes.ok) {
            console.error('[PBPCart] Failed to add gift to cart:', addData);
          } else {
            console.info('[PBPCart] Gift added to cart, quantity:', desiredQty);
          }
          this._internalAdd = false;
          await this.refresh();
        } else if (giftLine && giftLine.quantity !== desiredQty) {
          /* Gift line exists but wrong quantity (too many, too few, or
             should be removed entirely) — set it to the exact right
             number in one call. */
          this._internalAdd = true;
          await _fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: giftLine.key, quantity: desiredQty })
          });
          this._internalAdd = false;
          await this.refresh();
        }
      } catch (err) {
        this._internalAdd = false;
        console.warn('[PBPCart] gift eligibility check failed', err);
      }
    },

    async updateBubble() {
      try {
        const cart = await _fetch('/cart.js').then(r => r.json());
        const n = cart.item_count;
        document.querySelectorAll(
          '#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"],' +
          '.cart-count-bubble span[aria-hidden="true"]'
        ).forEach(el => { el.textContent = n; });
        document.querySelectorAll(
          '#cart-icon-bubble .cart-count-bubble span:not([aria-hidden]),' +
          '.cart-count-bubble span:not([aria-hidden])'
        ).forEach(el => { el.textContent = n + ' items'; });
      } catch (_) {}
    },

    async updateQty(key, qty) {
      this.spin(true);
      try {
        const res = await _fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity: Math.max(0, qty),
            sections: this.sectionId,
            sections_url: window.location.pathname
          })
        });
        const data = await res.json();
        if (!this.applySectionHtml(data.sections && data.sections[this.sectionId])) {
          await this.refresh();
        }
        await this.checkGiftEligibility();
      } catch (err) {
        console.warn('[PBPCart] updateQty failed', err);
      } finally {
        this.spin(false);
      }
    },

    async removeItem(key) {
      await this.updateQty(key, 0);
    },

    async addToCart(variantId, btn) {
      if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }
      this.spin(true);
      this._internalAdd = true;
      try {
        const res = await _fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            sections: this.sectionId,
            sections_url: window.location.pathname
          })
        });
        const data = await res.json();
        if (!this.applySectionHtml(data.sections && data.sections[this.sectionId])) {
          await this.refresh();
        }
        this._internalAdd = false;
        await this.checkGiftEligibility();
        this.open();
      } catch (err) {
        this._internalAdd = false;
        console.warn('[PBPCart] addToCart failed', err);
      } finally {
        this.spin(false);
        if (btn) { btn.disabled = false; btn.textContent = 'Add To Cart'; }
      }
    },

    scrollCarousel(dir) {
      const c    = document.getElementById('PBP-Carousel');
      if (!c) return;
      const card = c.querySelector('.pbp-upsell-card');
      c.scrollBy({ left: dir * ((card ? card.offsetWidth : 268) + 10), behavior: 'smooth' });
      setTimeout(() => this.updateNavBtns(), 320);
    },

    updateNavBtns() {
      const c    = document.getElementById('PBP-Carousel');
      const prev = document.getElementById('PBP-CarouselPrev');
      const next = document.getElementById('PBP-CarouselNext');
      if (!c || !prev || !next) return;
      prev.disabled = c.scrollLeft <= 2;
      next.disabled = c.scrollLeft + c.clientWidth >= c.scrollWidth - 2;
    },

    /* Intercept Quick Add form submits */
    bindQuickAdd() {
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('form[data-type="add-to-cart-form"]');
        if (!form) return;

        e.preventDefault();
        e.stopImmediatePropagation();

        const idInput = form.querySelector('input[name="id"]');
        if (!idInput) return;

        const variantId = idInput.value || idInput.getAttribute('value');
        const btn = form.querySelector('button[type="submit"]');

        if (btn) {
          btn.disabled = true;
          btn.querySelector('span')?.setAttribute('style', 'opacity:0.5');
          btn.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
        }

        this._internalAdd = true;
        try {
          const res = await _fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
              sections: this.sectionId,
              sections_url: window.location.pathname
            })
          });
          const data = await res.json();
          if (!this.applySectionHtml(data.sections && data.sections[this.sectionId])) {
            await this.refresh();
          }
          this._internalAdd = false;
          await this.checkGiftEligibility();
          this.open();
        } catch (err) {
          this._internalAdd = false;
          console.warn('[PBPCart] quick add failed', err);
        } finally {
          if (btn) {
            btn.disabled = false;
            btn.querySelector('span')?.removeAttribute('style');
            btn.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
          }
        }
      }, true);
    },

    bindTriggers() {
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (cartIcon && !cartIcon._pbpBound) {
        cartIcon._pbpBound = true;
        cartIcon.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.open();
        }, true);
      }
    },

    init() {
      this.bindTriggers();
      this.bindQuickAdd();

      const header = document.querySelector('header, #shopify-section-header, .header-wrapper');
      if (header) {
        new MutationObserver(() => this.bindTriggers())
          .observe(header, { childList: true, subtree: true });
      }

      document.getElementById('PBP-Carousel')
        ?.addEventListener('scroll', () => this.updateNavBtns());

      ['cart:refresh', 'cart:updated', 'cart:add'].forEach(ev =>
        document.addEventListener(ev, () => this.syncCart())
      );

      /* Sync once on page load in case the cart already qualifies
         (e.g. a checkout-abandon return, or a page refresh). */
      this.checkGiftEligibility();
    }
  };

  /* ── Init ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => PBPCart.init());
  } else {
    PBPCart.init();
  }

})();